import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import StudentShell from "../components/StudentShell/StudentShell";
import { apiBaseUrl } from "../services/auth";
import "./Contact.css";

// Same backend as the rest of the app (REACT_APP_BACKEND_URL).
const API_BASE_URL = apiBaseUrl;

const initialFormState = {
  fullName: "",
  email: "",
  phoneNumber: "",
  subject: "",
  message: "",
};

function Contact() {
  const { user, effectiveRole } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [formData, setFormData] = useState(initialFormState);
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const isConnected = Boolean(user);

  const topicLabel = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search);

      const explicit = params.get("topic") || params.get("subject");
      if (explicit) return explicit;

      const addon = params.get("addon");
      if (addon) {
        const addonKeyByValue = {
          coaching: "offers.addons.coaching.title",
          assessment: "offers.addons.assessment.title",
          "1to1": "offers.addons.oneToOne.title",
        };
        const key = addonKeyByValue[addon];
        if (key) {
          const translated = t(key);
          return translated === key ? `addon: ${addon}` : translated;
        }
        return `addon: ${addon}`;
      }

      const classType = params.get("class");
      if (classType) {
        const classKeyByValue = {
          full: "offers.classTypes.full.title",
          duo: "offers.classTypes.duo.title",
          vip: "offers.classTypes.vip.title",
          normal: "offers.classTypes.normal.title",
        };
        const key = classKeyByValue[classType];
        if (key) {
          const translated = t(key);
          return translated === key ? `class: ${classType}` : translated;
        }
        return `class: ${classType}`;
      }

      const lang = params.get("lang");
      if (lang) {
        const langKeyByValue = {
          french: "offers.languagePrograms.french.title",
          english: "offers.languagePrograms.english.title",
          arabic: "offers.languagePrograms.arabic.title",
        };
        const key = langKeyByValue[lang];
        if (key) {
          const translated = t(key);
          return translated === key ? `language: ${lang}` : translated;
        }
        return `language: ${lang}`;
      }

      const upcoming = params.get("upcoming");
      if (upcoming) {
        const upcomingKeyByValue = {
          coding: "offers.upcoming.coding.title",
          ai: "offers.upcoming.ai.title",
          "future-leaders": "offers.upcoming.leaders.title",
        };
        const key = upcomingKeyByValue[upcoming];
        if (key) {
          const translated = t(key);
          return translated === key ? `upcoming: ${upcoming}` : translated;
        }
        return `upcoming: ${upcoming}`;
      }

      const plan = params.get("plan");
      if (plan) {
        const planLabelByValue = {
          bronze: "Bronze Plan",
          silver: "Silver Plan",
          gold: "Gold Plan",
          platinum: "Platinum Plan",
        };
        return planLabelByValue[plan] || `plan: ${plan}`;
      }
    } catch (e) {
      // ignore
    }

    return "";
  }, [location.search, t]);

  const sourceLabel = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search);
      const explicit = params.get("from");
      if (explicit) return explicit;
    } catch (e) {
      // ignore
    }

    try {
      const prev = sessionStorage.getItem("casaclass:prevRoute");
      if (prev && !String(prev).startsWith("/contact")) return prev;
    } catch (e) {
      // ignore
    }

    try {
      if (typeof document !== "undefined" && document.referrer) {
        const ref = new URL(document.referrer);
        if (ref.origin === window.location.origin) {
          const path = `${ref.pathname}${ref.search}`;
          if (path && !String(path).startsWith("/contact")) return path;
        }
      }
    } catch (e) {
      // ignore
    }

    return "";
  }, [location.search]);

  const buildSubject = (subject) => {
    const cleanSubject = (subject || "").trim();
    const base = cleanSubject || (topicLabel || "").trim();

    const parts = [];
    if (base) parts.push(base);

    if (topicLabel) {
      const topicTag = `topic: ${topicLabel}`;
      const alreadyHasTopic = `${base}`
        .toLowerCase()
        .includes(String(topicLabel).toLowerCase());
      if (!alreadyHasTopic) parts.push(`(${topicTag})`);
    }

    if (sourceLabel) {
      const fromTag = `from: ${sourceLabel}`;
      const normalized = `${base}`.toLowerCase();
      if (!normalized.includes(fromTag.toLowerCase())) {
        parts.push(`(${fromTag})`);
      }
    }

    return parts.join(" ").trim();
  };

  useEffect(() => {
    if (!topicLabel && !sourceLabel) return;
    setFormData((prev) => {
      if (prev.subject && prev.subject.trim()) return prev;
      return {
        ...prev,
        // Show just the topic (e.g. the chosen offer); the source tag is still
        // added by buildSubject when the message is sent.
        subject: topicLabel ? topicLabel : buildSubject(""),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicLabel, sourceLabel]);

  useEffect(() => {
    if (!isConnected) return;
    setFormData((prev) => ({
      ...prev,
      fullName:
        user?.full_name ||
        `${user?.first_name || ""} ${user?.last_name || ""}`.trim(),
      email: user?.email || "",
      phoneNumber: user?.phone_number || "",
    }));
  }, [isConnected, user]);

  useEffect(() => {
    const existingScript = document.querySelector("script[data-calendly]");
    if (existingScript) {
      return undefined;
    }

    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    script.defer = true;
    script.setAttribute("data-calendly", "true");
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const updateField = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ state: "loading", message: t("contactPage.status.loading") });

    try {
      const resolvedFullName = isConnected
        ? user?.full_name ||
          `${user?.first_name || ""} ${user?.last_name || ""}`.trim()
        : formData.fullName;
      const resolvedEmail = isConnected ? user?.email : formData.email;
      const resolvedPhone = isConnected
        ? user?.phone_number
        : formData.phoneNumber;

      const payloadToSend = {
        fullName: resolvedFullName,
        email: resolvedEmail,
        subject: buildSubject(formData.subject),
        message: resolvedPhone
          ? `${formData.message}\n\n---\n${t("contactPage.form.phoneAppend", {
              phone: resolvedPhone,
            })}`
          : formData.message,
      };

      const response = await fetch(`${API_BASE_URL}/api/contact/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payloadToSend),
      });

      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload?.error || t("contactPage.status.error"));
      }

      setStatus({
        state: "success",
        message: payload?.message || t("contactPage.status.success"),
      });
      setFormData((prev) => ({
        ...initialFormState,
        ...(isConnected
          ? {
              fullName: resolvedFullName || "",
              email: resolvedEmail || "",
              phoneNumber: resolvedPhone || "",
            }
          : {}),
      }));
    } catch (error) {
      setStatus({
        state: "error",
        message: error.message || t("contactPage.status.error"),
      });
    }
  };

  const isSubmitting = status.state === "loading";

  const page = (
    <main className={`contact-page${isConnected ? " contact-page--app" : ""}`}>
      <section className="contact-hero">
        <div className="contact-hero__content">
          <p className="section-label">{t("contactPage.hero.label")}</p>
          <h1 className="section-title">{t("contactPage.hero.title")}</h1>
          <p className="section-lead">{t("contactPage.hero.lead")}</p>
        </div>
      </section>

      <section className="contact-grid">
        <article className="contact-card">
          <h2 className="contact-card__title">
            {t("contactPage.form.cardTitle")}
          </h2>
          <p className="contact-card__subtitle">
            {t("contactPage.form.cardSubtitle")}
          </p>
          <form className="contact-form" onSubmit={handleSubmit}>
            {!isConnected && (
              <>
                <label className="form-label" htmlFor="fullName">
                  {t("contactPage.form.fullNameLabel")}
                </label>
                <input
                  className="form-input"
                  id="fullName"
                  name="fullName"
                  type="text"
                  placeholder={t("contactPage.form.fullNamePlaceholder")}
                  value={formData.fullName}
                  onChange={updateField}
                  required
                  autoComplete="name"
                  disabled={isSubmitting}
                />

                <label className="form-label" htmlFor="email">
                  {t("contactPage.form.emailLabel")}
                </label>
                <input
                  className="form-input"
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t("contactPage.form.emailPlaceholder")}
                  value={formData.email}
                  onChange={updateField}
                  required
                  autoComplete="email"
                  inputMode="email"
                  disabled={isSubmitting}
                />

                <label className="form-label" htmlFor="phoneNumber">
                  {t("contactPage.form.phoneLabel")}
                </label>
                <input
                  className="form-input"
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  placeholder={t("contactPage.form.phonePlaceholder")}
                  value={formData.phoneNumber}
                  onChange={updateField}
                  autoComplete="tel"
                  inputMode="tel"
                  disabled={isSubmitting}
                />
              </>
            )}

            <label className="form-label" htmlFor="subject">
              {t("contactPage.form.subjectLabel")}
            </label>
            <input
              className="form-input"
              id="subject"
              name="subject"
              type="text"
              placeholder={t("contactPage.form.subjectPlaceholder")}
              value={formData.subject}
              onChange={updateField}
              disabled={isSubmitting}
            />

            <label className="form-label" htmlFor="message">
              {t("contactPage.form.messageLabel")}
            </label>
            <textarea
              className="form-textarea"
              id="message"
              name="message"
              rows="5"
              placeholder={t("contactPage.form.messagePlaceholder")}
              value={formData.message}
              onChange={updateField}
              required
              disabled={isSubmitting}
            />

            {status.state !== "idle" && (
              <p className={`form-status form-status--${status.state}`}>
                {status.message}
              </p>
            )}

            <button
              className="form-submit"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? t("contactPage.form.submitting")
                : t("contactPage.form.submit")}
            </button>
          </form>
        </article>

        <article className="calendly-card">
          <h2 className="contact-card__title">
            {t("contactPage.calendly.title")}
          </h2>
          <p className="contact-card__subtitle">
            {t("contactPage.calendly.subtitle")}
          </p>
          <div
            className="calendly-inline-widget"
            data-url="https://calendly.com/casaclass1/30min?hide_event_type_details=1&hide_gdpr_banner=1"
          />
        </article>
      </section>
    </main>
  );

  return effectiveRole === "student" ? (
    <StudentShell>{page}</StudentShell>
  ) : (
    page
  );
}

export default Contact;
